import { useCallback, useEffect } from 'react';
import {
  useAuthStore,
  useAuthtoken,
  useSelectedEntityId,
  getAuthtoken,
  logout,
  setSelectedOrganizationId,
  type SelectedEntityId,
} from 'stores/useAuthStore';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import moment from 'moment-timezone';

import countries from 'utils/countries';
import hostingPath from 'utils/hostingPath';
import request from 'utils/request';
import replaceByKeyName from 'utils/mockFn';
import { TOKEN_INVALID } from 'containers/Base/useBaseViewData';
import {
  openOrgSetupWizard,
  setAppLoading,
  setOrgSetupCreateError,
  setWebDialog,
  showSnackbar,
 useMaskMode } from 'stores/useUiStore';
import { getUser, setUser } from 'stores/useSessionStore';
import {
  getOrganizations,
  getOrganizationSettingsMap,
  resetOrganizations,
  setOrganizations,
  setOrganizationSettingsMap,
} from 'stores/useOrganizationsStore';
import { getSystemScheduledTasks } from 'containers/ScheduledTasks/scheduledTaskTemplates';
import type { ApiRespBody } from 'types/api';
import type {
  AllPossibleIntegrationSettings,
  IntegrationERP,
  Location,
  Organization,
  OrganizationsState,
  TOrgPredicateState,
} from '../organizations.types';

export const ORGANIZATIONS_QUERY_KEY = ['organizations'] as const;

type OrgSettingsMap = NonNullable<
  OrganizationsState['organizationSettingsMapByOrgId']
>;

export interface OrganizationsFetchResult {
  organizations: Organization[];
  organizationSettingsMapByOrgId: OrgSettingsMap;
}

export async function fetchOrganizations(
  authToken: string,
  selectedEntityId: SelectedEntityId,
  signal?: AbortSignal
): Promise<OrganizationsFetchResult> {
  const result = await request<ApiRespBody<OrganizationsFetchResult>>(
    `/api/organizations?entityId=${selectedEntityId}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', authorization: authToken },
      ...(signal ? { signal } : {}),
    }
  );

  if (result?.success) {
    return {
      organizations: result.data?.organizations ?? [],
      organizationSettingsMapByOrgId:
        result.data?.organizationSettingsMapByOrgId ?? {},
    };
  }
  if (result?.message === TOKEN_INVALID) throw new Error(TOKEN_INVALID);
  throw new Error(result?.message || 'Failed to load organizations');
}

/**
 * `replaceByKeyName` mutates in place, so clone each row first — the source is
 * the shared React Query cache and must stay pristine.
 */
function maskOrganizations(organizations: Organization[]): Organization[] {
  return organizations.map((org) => {
    const masked = { ...org, organizationName: org.organizationName };
    replaceByKeyName(masked, 'organizationName', 'organization');
    return masked;
  });
}

export function organizationsQueryOptions(
  authToken: string,
  selectedEntityId: SelectedEntityId
) {
  return queryOptions({
    queryKey: [...ORGANIZATIONS_QUERY_KEY, selectedEntityId],
    queryFn: ({ signal }) =>
      fetchOrganizations(authToken, selectedEntityId, signal),
    enabled: !!authToken && Number.isFinite(selectedEntityId),
    staleTime: Infinity,
  });
}

export function useOrganizations() {
  const authToken = useAuthtoken();
  const selectedEntityId = useSelectedEntityId();
  const maskMode = useMaskMode();

  const query = useQuery(
    organizationsQueryOptions(authToken, selectedEntityId)
  );

  useEffect(() => {
    if (!(query.error instanceof Error)) return;
    if (query.error.message === TOKEN_INVALID) {
      logout();
    } else {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onOrganizationsGet',
          open: true,
          message: query.error.message,
          severity: 'error',
        });
    }
  }, [query.error]);

  const { data, dataUpdatedAt } = query;
  useEffect(() => {
    resetOrganizations();
  }, [selectedEntityId]);

  useEffect(() => {
    if (!data) return;
    const values = maskMode
      ? maskOrganizations(data.organizations)
      : data.organizations;
    setOrganizations({
      lastUpdated: new Date(dataUpdatedAt || Date.now()).toISOString(),
      values,
    });
    setOrganizationSettingsMap(data.organizationSettingsMapByOrgId);
  }, [data, dataUpdatedAt, maskMode]);

  return query;
}

export function useRefreshOrganizations() {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY }),
    [queryClient]
  );
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Polls `/configure?refresh=true` until the Node-RED flow is deployed. 60 polls ×
 * 10s = up to 10 minutes.
 */
async function pollForProvisioningComplete(
  authToken: string,
  organizationId: Organization['organizationId'],
  pollsRemaining = 60
): Promise<boolean> {
  const pollingDelay = 10000;
  try {
    const res = await request<ApiRespBody>(
      `/api/organizations/${organizationId}/configure?refresh=true`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          authorization: authToken,
        },
      }
    );
    if (res?.success) return true;
  } catch {
    await sleep(pollingDelay);
    return pollsRemaining > 1
      ? pollForProvisioningComplete(
          authToken,
          organizationId,
          pollsRemaining - 1
        )
      : false;
  }
  await sleep(pollingDelay);
  return pollsRemaining > 1
    ? pollForProvisioningComplete(
        authToken,
        organizationId,
        pollsRemaining - 1
      )
    : false;
}

export function erpDefaultVersion(erpSelected: string): string {
  switch (erpSelected) {
    case 'Epicor':
      return 'kinetic';
    case 'JobBoss':
      return 'classic';
    case 'Abas':
      return 'rest';
    case 'Fishbowl':
    case 'VisualEstiTrack':
      return 'odbc';
    default:
      return 'default';
  }
}

export function defaultCountryForTimezone(timezone: string): string {
  let countryAbbr = 'US';
  countries.forEach((country) => {
    if (country.zones.includes(timezone)) countryAbbr = country.abbr;
  });
  return countryAbbr;
}

function waitForTokenRemint(timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const prevToken = getAuthtoken();
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      unsubscribe();
      clearTimeout(timer);
      resolve();
    };
    const unsubscribe = useAuthStore.subscribe((state) => {
      if (state.authtoken !== prevToken) finish();
    });
    const timer = setTimeout(finish, timeoutMs);
  });
}

export function useLocationAdd() {
  const authToken = useAuthtoken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: {
      organization: Organization;
      location: Omit<Location, 'locationId' | 'organizationId'>;
    }) =>
      request<ApiRespBody>('/api/locations', {
        method: 'POST',
        body: JSON.stringify({
          ...vars.location,
          organizationId: vars.organization.organizationId,
        }),
        headers: { 'Content-Type': 'application/json', authorization: authToken },
      }),
    onSuccess: (result) => {
      if (result?.success) {
        queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
      } else if (result?.message === TOKEN_INVALID) {
        logout();
      } else {
        showSnackbar({
            autoHideDuration: null,
            caller: 'onLocationAdd',
            open: true,
            message: result?.message ?? 'Request failed',
            severity: 'error',
          });
      }
    },
    onError: (err: unknown) => {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onLocationAdd',
          open: true,
          message: err instanceof Error ? err.message : 'Request failed',
          severity: 'error',
        });
    },
  });
}

export function useLocationDelete() {
  const authToken = useAuthtoken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (location: Location) =>
      request<ApiRespBody>(`/api/locations/${location.locationId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', authorization: authToken },
      }),
    onSuccess: (result) => {
      if (result?.success) {
        queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
        showSnackbar({
            autoHideDuration: 5000,
            caller: 'onLocationDelete',
            open: true,
            message: 'Location Deleted',
            severity: 'success',
          });
      } else if (result?.message === TOKEN_INVALID) {
        logout();
      } else {
        showSnackbar({
            autoHideDuration: null,
            caller: 'onLocationDelete',
            open: true,
            message: result?.message ?? 'Request failed',
            severity: 'error',
          });
      }
    },
    onError: (err: unknown) => {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onLocationDelete',
          open: true,
          message: err instanceof Error ? err.message : 'Request failed',
          severity: 'error',
        });
    },
  });
}

export function useLocationUpdate() {
  const authToken = useAuthtoken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (location: Location) =>
      request<ApiRespBody>('/api/locations', {
        method: 'PUT',
        body: JSON.stringify(location),
        headers: { 'Content-Type': 'application/json', authorization: authToken },
      }),
    onSuccess: (result) => {
      if (result?.success) {
        queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
      } else if (result?.message === TOKEN_INVALID) {
        logout();
      } else {
        showSnackbar({
            autoHideDuration: null,
            caller: 'onLocationUpdate',
            open: true,
            message: result?.message ?? 'Request failed',
            severity: 'error',
          });
      }
    },
    onError: (err: unknown) => {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onLocationUpdate',
          open: true,
          message: err instanceof Error ? err.message : 'Request failed',
          severity: 'error',
        });
    },
  });
}

export function useOrganizationDelete() {
  const authToken = useAuthtoken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (organization: Organization) =>
      request<ApiRespBody>(
        `/api/organizations/${organization.organizationId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            authorization: authToken,
          },
        }
      ),
    onSuccess: (result) => {
      if (result?.success) {
        queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
        showSnackbar({
            autoHideDuration: 5000,
            caller: 'onOrganizationDelete',
            open: true,
            message: 'Organization Deleted',
            severity: 'success',
          });
      } else if (result?.message === TOKEN_INVALID) {
        logout();
      } else {
        showSnackbar({
            autoHideDuration: null,
            caller: 'onOrganizationDelete',
            open: true,
            message: result?.message ?? 'Request failed',
            severity: 'error',
          });
      }
    },
    onError: (err: unknown) => {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onOrganizationDelete',
          open: true,
          message: err instanceof Error ? err.message : 'Request failed',
          severity: 'error',
        });
    },
  });
}

export function useOrganizationCustomize() {
  const authToken = useAuthtoken();

  return useMutation({
    mutationFn: async (organization: Organization) => {
      const result = await request<ApiRespBody<{ token: string }>>(
        `/api/organizations/${organization.organizationId}/configure`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            authorization: authToken,
          },
        }
      );
      return { result, organization };
    },
    onSuccess: ({ result, organization }) => {
      if (result?.success) {
        setWebDialog({
            show: true,
            url: hostingPath('nodered', '', result.data?.token),
            label: `Customize ${organization.organizationName}`,
          });
      } else {
        showSnackbar({
            autoHideDuration: null,
            caller: 'onOrganizationCustomize',
            open: true,
            message: result?.message ?? 'Request failed',
            severity: 'error',
          });
      }
    },
    onError: (err: unknown) => {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onOrganizationCustomize',
          open: true,
          message: err instanceof Error ? err.message : 'Request failed',
          severity: 'error',
        });
    },
  });
}

export function useOrganizationFlowRefresh() {
  const authToken = useAuthtoken();

  return useMutation({
    mutationFn: (organization: Organization) =>
      request<ApiRespBody>(
        `/api/organizations/${organization.organizationId}/configure?refresh=true`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            authorization: authToken,
          },
        }
      ),
    onSuccess: (result) => {
      if (result?.success) {
        showSnackbar({
            autoHideDuration: 5000,
            caller: 'onOrganizationFlowRefresh',
            open: true,
            message: 'Organization Integration Refreshed',
            severity: 'success',
          });
      } else if (result?.message === TOKEN_INVALID) {
        logout();
      } else {
        showSnackbar({
            autoHideDuration: null,
            caller: 'onOrganizationFlowRefresh',
            open: true,
            message: result?.message ?? 'Request failed',
            severity: 'error',
          });
      }
    },
    onError: (err: unknown) => {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onOrganizationFlowRefresh',
          open: true,
          message: err instanceof Error ? err.message : 'Request failed',
          severity: 'error',
        });
    },
  });
}

export function useOrganizationUpdate() {
  const authToken = useAuthtoken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      organization: Organization;
      organizationSettings: AllPossibleIntegrationSettings;
    }) => {
      const orgUpdResp = await request<ApiRespBody>('/api/organizations', {
        method: 'PUT',
        body: JSON.stringify(vars.organization),
        headers: { 'Content-Type': 'application/json', authorization: authToken },
      });
      if (!orgUpdResp?.success) return { orgUpdResp, settingsUpdResp: null };
      const settingsUpdResp = await request<ApiRespBody>(
        `/api/organizations/${vars.organization.organizationId}/settings`,
        {
          method: 'POST',
          body: JSON.stringify(vars.organizationSettings),
          headers: {
            'Content-Type': 'application/json',
            authorization: authToken,
          },
        }
      );
      return { orgUpdResp, settingsUpdResp };
    },
    onSuccess: ({ orgUpdResp, settingsUpdResp }) => {
      if (orgUpdResp?.success) {
        if (settingsUpdResp?.success) {
          queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
        }
      } else if (orgUpdResp?.message === TOKEN_INVALID) {
        logout();
      } else {
        showSnackbar({
            autoHideDuration: null,
            caller: 'onOrganizationUpdate',
            open: true,
            message: orgUpdResp?.message ?? 'Request failed',
            severity: 'error',
          });
      }
    },
    onError: (err: unknown) => {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onOrganizationUpdate',
          open: true,
          message: err instanceof Error ? err.message : 'Request failed',
          severity: 'error',
        });
    },
  });
}

async function createScheduledTasks(
  authToken: string,
  scheduledTasks: ReturnType<typeof getSystemScheduledTasks>,
  index = 0
): Promise<void> {
  const scheduledTask = scheduledTasks[index];
  if (!scheduledTask) return;

  const response = await request<ApiRespBody>('/api/scheduledTasks', {
    method: 'POST',
    body: JSON.stringify(scheduledTask),
    headers: {
      'Content-Type': 'application/json',
      authorization: authToken,
    },
  });
  if (!response?.success) {
    throw new Error('Scheduled task creation failure');
  }
  await createScheduledTasks(authToken, scheduledTasks, index + 1);
}

export function useOrganizationAdd() {
  const authToken = useAuthtoken();
  const selectedEntityId = useSelectedEntityId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      organization: TOrgPredicateState;
      organizationSettings: AllPossibleIntegrationSettings;
      location: Omit<Location, 'locationId' | 'organizationId'>;
    }) => {
      setAppLoading({ isLoading: true, label: 'Creating Organization' });
      try {
        const provisionedResp = await request<
          ApiRespBody<{
            organizationId: Organization['organizationId'];
            newOrganizationToken: string;
          }>
        >('/api/organizations', {
          method: 'POST',
          body: JSON.stringify({
            ...vars.organization,
            entityId: selectedEntityId,
            location: JSON.stringify({
              ...vars.location,
              entityId: selectedEntityId,
            }),
          }),
          headers: {
            'Content-Type': 'application/json',
            authorization: authToken,
          },
        });
        if (!provisionedResp?.success || !provisionedResp.data) {
          throw new Error('Provisioning failure');
        }
        const { organizationId } = provisionedResp.data;

        const provisioningComplete = await pollForProvisioningComplete(
          authToken,
          organizationId
        );
        if (!provisioningComplete) {
          throw new Error('Provisioning completion failure');
        }

        const settingsResp = await request<ApiRespBody>(
          `/api/organizations/${organizationId}/settings`,
          {
            method: 'POST',
            body: JSON.stringify(vars.organizationSettings),
            headers: {
              'Content-Type': 'application/json',
              authorization: authToken,
            },
          }
        );
        if (!settingsResp?.success) throw new Error('Settings update failure');

        const localizedSystemTasks = getSystemScheduledTasks(
          vars.organizationSettings.timeZone
        );
        await createScheduledTasks(authToken, localizedSystemTasks);

        return organizationId;
      } finally {
        setAppLoading({ isLoading: false });
      }
    },
    onSuccess: (organizationId) => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
      openOrgSetupWizard(organizationId);
    },
    onError: (err: unknown) => {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onOrganizationAdd',
          open: true,
          message: `An error occurred while creating the organization: ${
            err instanceof Error ? err.message : String(err)
          }`,
          severity: 'error',
        });
    },
  });
}

export function useOrganizationCreate() {
  const authToken = useAuthtoken();
  const selectedEntityId = useSelectedEntityId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      organizationName: string;
      erpSelected: IntegrationERP['ERP.selected'];
    }) => {
      const { organizationName, erpSelected } = vars;
      setAppLoading({ isLoading: true, label: 'Creating Organization' });
      try {
        const guessedTimezone = moment.tz.guess();
        const provisionResp = await request<
          ApiRespBody<{
            organizationId: Organization['organizationId'];
            newOrganizationToken: string;
          }>
        >('/api/organizations', {
          method: 'POST',
          body: JSON.stringify({
            organizationName,
            monitored: 0,
            entityId: selectedEntityId,
            location: JSON.stringify({
              locationName: 'Default',
              entityId: selectedEntityId,
              country: defaultCountryForTimezone(guessedTimezone),
              timezone: guessedTimezone,
            }),
          }),
          headers: {
            'Content-Type': 'application/json',
            authorization: authToken,
          },
        });
        if (!provisionResp?.success || !provisionResp.data?.organizationId) {
          throw new Error(provisionResp?.message || 'Provisioning failure');
        }
        const { organizationId } = provisionResp.data;

        const provisioningComplete = await pollForProvisioningComplete(
          authToken,
          organizationId
        );
        if (!provisioningComplete) {
          throw new Error(
            'Provisioning timed out — try again or contact support'
          );
        }

        const minimalSettings: AllPossibleIntegrationSettings = {
          'ERP.selected': erpSelected,
          [`ERP.${erpSelected}.version`]: erpDefaultVersion(erpSelected),
          timeZone: guessedTimezone,
        };
        const settingsResp = await request<ApiRespBody>(
          `/api/organizations/${organizationId}/settings`,
          {
            method: 'POST',
            body: JSON.stringify(minimalSettings),
            headers: {
              'Content-Type': 'application/json',
              authorization: authToken,
            },
          }
        );
        if (!settingsResp?.success) {
          console.warn(
            '[useOrganizationCreate] /initialize did not succeed:',
            settingsResp?.message
          );
          showSnackbar({
              autoHideDuration: 8000,
              caller: 'onOrganizationCreate',
              open: true,
              message:
                'Organization created, but integration tables could not be ' +
                'initialized: ' +
                (settingsResp?.message ||
                  'unknown error from the integration service') +
                '. Continue through the wizard; the ERP Connection step ' +
                'retries it when you press Next.',
              severity: 'warning',
            });
        }

        const currentSettingsMap = getOrganizationSettingsMap() ?? {};
        setOrganizationSettingsMap({
          ...currentSettingsMap,
          [organizationId]: minimalSettings,
        });

        const orgsState = getOrganizations();
        const existingValues = orgsState?.values ?? [];
        const alreadyPresent = existingValues.some(
          (o) => o.organizationId === organizationId
        );
        if (!alreadyPresent) {
          const stubOrg: Organization = {
            organizationId,
            organizationName,
            entityId: selectedEntityId ?? undefined,
            locations: [],
            monitored: 0,
            setupRequired: true,
          };
          setOrganizations({
            lastUpdated: new Date().toISOString(),
            values: [...existingValues, stubOrg],
          });
        }

        const currentUser = getUser();
        if (currentUser) {
          setUser({
            ...currentUser,
            selectedOrganizationId: organizationId,
          });
        }

        setSelectedOrganizationId(organizationId);

        await waitForTokenRemint(15000);

        openOrgSetupWizard(organizationId);

        queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });

        return organizationId;
      } finally {
        setAppLoading({ isLoading: false });
      }
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('[useOrganizationCreate]', err);
      setOrgSetupCreateError(message);
    },
  });
}
